import {action, computed, observable} from "mobx";
import {FeedItemEntity} from "./entities/FeedItemEntity";
import {API} from "../api";
import {UIState} from "./UIState";
import {dataURItoBlob, stringOrEmptyString} from "../utils";
import * as moment from "moment/moment";
import {BundleData} from "./BundleData";

export interface CommentDataInterface {
  comment:string;
  photo?:any;
  parentCommentId?:string;
}


class _FullPostModalData {
  NO_BUNDLE_ID = "";

  @observable showFullPostModal:boolean = false;
  @observable post:any;
  @observable feedItem:any;
  @observable solutionId = '';
  @observable bundleId = this.NO_BUNDLE_ID;
  @observable private _author:any;
  @observable openingFullPostModal = false;
  @observable comments:any[] = [];
  @observable commentsLoadedSuccessfully = false;

  @action loadComments = () => {
    API.get(`/posts/${this.post._id}/comments`)
      .then((comments)=>{
        this.comments = comments;
        this.commentsLoadedSuccessfully = true;
      })
      .catch((err)=>{
        console.log(err);
        this.comments = [];
      });
  }


  /**
   * Adds a comment or a reply, and updates all datastores that reference the
   * post with the comments.
   *
   * @param data
   */
  @action addComment = (data:CommentDataInterface) => new Promise<void>((resolve, reject)=> {
    if (!data.comment && !data.photo) {
      reject("One of comment or photo is required");
    } else {

      const formData = new FormData();
      formData.append("comment", stringOrEmptyString(data.comment));

      if (undefined != data.parentCommentId) {
        formData.append("parentCommentId", stringOrEmptyString(data.parentCommentId));
      }

      if (undefined != this.solutionId) {
        formData.append("solutionId", stringOrEmptyString(this.solutionId));
      }

      if (undefined != this.bundleId) {
        formData.append("bundleId", stringOrEmptyString(this.bundleId));
      }

      if (undefined != data.photo) {
        const name = "comment_" + moment().format('YYYY-MM-DD-HH-mm-ss') + ".jpg";
        formData.append("file", dataURItoBlob(data.photo), name);
      }

      API.postMultipart(`/posts/${this.post._id}/comments`, formData)
      .then((response) => {
        if(data.parentCommentId) {
          const comment = this.comments.find((c)=>c._id===data.parentCommentId);
          if(comment) {
            if(!comment.replies) {
              comment.replies = [];
            }
            comment.replies.push(response.comment);
          }
          resolve();
        } else {

          // Add the newly created comment
          this.comments.push(response.comment);
          this.post.comments.push(response.comment);


          // For Participants:
          if(UIState.user) {
            // if a feedItem was created on the server side
            if(response.feedItem) {
              if(UIState.feedData) {
                UIState.feedData.addFeedItem(response.feedItem);
              }
            }



            if(this.feedItem) {
              // If there is a point value for commenting and there is a feed item, award the points.
              // No points are awarded for replies, which are denoted by the parentCommentId
              if (this.post.pointValue > 0 && this.post.awardPointsForCommenting && !this.feedItem.pointsAwarded) {
                const feedItemUpdates = {
                  pointsAwarded: this.feedItem._post.pointValue
                };
                API.put(`/feed/items/${this.feedItem._id}`, feedItemUpdates)
                .then((_feedItem) => {
                  let feedItem = this.feedItem;
                  feedItem.pointsAwarded = _feedItem.pointsAwarded;
                  feedItem.pointsAwardedDate = _feedItem.pointsAwardedDate;

                  // Since feed items are stored in a different datastore, we make sure to update
                  // the feed item when a user has added a comment, so that points and the comment count show
                  // up in the feed.
                  if(UIState.feedData) {
                    UIState.feedData.updateFeedItemInBrowser(feedItem);
                  }
                  if(BundleData.selectedBundle) {
                    BundleData.selectedBundle.updateAnyRelatedPostComments(feedItem._post);
                  }
                  resolve();
                })
                .catch((err) => {
                  reject(err);
                });
              }
              // a feedItem exists, but no points are to be awarded
              else {

                if(UIState.feedData) {
                  UIState.feedData.updateFeedItemInBrowser(this.feedItem);
                }
                if(BundleData.selectedBundle) {
                  BundleData.selectedBundle.updateAnyRelatedPostComments(this.feedItem._post);
                }
                resolve();
              }
            }
            // No feedItem, only a post displayed in the modal
            else {
              if(BundleData.selectedBundle) {
                BundleData.selectedBundle.updateAnyRelatedPostComments(this.post);
              }
              resolve();
            }

          }  else {
            resolve();
          }

        }
      })
      .catch((err) => {
        reject(err);
      });
    }
  });


  @action openFullPostModal = (post:any,  solutionId:string, bundleId:string, author?:any) => {
    if(!this.openingFullPostModal) {
      this.openingFullPostModal = true;
      this.post = post;
      this.solutionId = solutionId;
      this.bundleId = bundleId;
      this._author = author;
      this.showFullPostModal = true;
      if(this.post.commenting) {
        this.loadComments();
      }
      setTimeout(action(()=>{
        this.openingFullPostModal = false;
      }), 250);
    }
  }

  @action openFeedItemInFullPostModal = (feedItem:FeedItemEntity, solutionId:string, bundleId:string) => {
    this.feedItem = feedItem;
    this.openFullPostModal(feedItem._post, solutionId, bundleId, feedItem._postAuthor);
  }

  @action closeFullPostModal = () => {
    this.showFullPostModal = false;
    setTimeout(action(()=>{
      this.feedItem = null;
      this.post = null;
      this.comments = [];
      this.solutionId = '';
      this.bundleId = '';
    }), 250);
  }


  @computed get author() {
    if(this._author) {
      return this._author;
    } else if(this.feedItem && this.feedItem._postAuthor) {
      return this.feedItem._postAuthor;
    } else {
      return null;
    }
  }




}

const FullPostModalData = new _FullPostModalData();
export default FullPostModalData;
